// Step routing of the "create article" wizard (step 0 "type" is skipped).

// Step name to index mapping (SKIPPING 'type' step - start from 'setup')
export const STEP_NAME_TO_INDEX = {
  'setup': 1,
  'content': 2,
  'sources': 3,
  'keywords': 4,
  'review': 5
};

// Step index to name mapping (SKIPPING step 0)
export const STEP_INDEX_TO_NAME = {
  1: 'setup',
  2: 'content',
  3: 'sources',
  4: 'keywords',
  5: 'review'
};

/**
 * Resolves the `:stepNumber` route param (step name or legacy 1-based number) to a step index.
 * Defaults to step 1 (setup).
 */
export const resolveStepNumber = (stepNumber) => {
  // Check if stepNumber is a valid step name
  if (stepNumber && STEP_NAME_TO_INDEX.hasOwnProperty(stepNumber)) {
    return STEP_NAME_TO_INDEX[stepNumber];
  }
  // Legacy support: if it's a number, convert it
  const urlStep = parseInt(stepNumber);
  if (stepNumber && !isNaN(urlStep) && urlStep >= 1 && urlStep <= 5) {
    return urlStep; // Keep 1-based indexing (1 = setup, 2 = content, etc.)
  }
  return 1; // Default to setup step (skip type selection)
};

export const CONTENT_TYPES = ['blogpost', 'changelog', 'summary'];

export const isChangelogType = (contentType) => contentType === 'changelog';

/**
 * Steps a content type goes through. A changelog describes the user's own
 * release, so scientific/web sources and SEO keyword research are skipped.
 */
export const getStepSequence = (contentType) => (
  isChangelogType(contentType) ? [1, 2, 5] : [1, 2, 3, 4, 5]
);

/** Maps a step the content type skips to the next step it does use. */
export const normalizeStepForContentType = (step, contentType) => {
  const sequence = getStepSequence(contentType);
  if (sequence.includes(step)) return step;
  return sequence.find((s) => s > step) || sequence[sequence.length - 1];
};

export const getNextStep = (currentStep, contentType) => (
  getStepSequence(contentType).find((s) => s > currentStep) || null
);

export const getPreviousStep = (currentStep, contentType) => (
  [...getStepSequence(contentType)].reverse().find((s) => s < currentStep) || null
);

const STEP_LABELS = {
  1: (t) => [t('articles.create.steps.setup.name'), t('articles.create.steps.setup.description')],
  2: (t) => [t('articles.create.steps.content.name'), t('articles.create.steps.content.description')],
  3: (t) => [t('articles.create.steps.sources.name', 'Sources'), t('articles.create.steps.sources.description', 'Choose the sources for the article')],
  4: (t) => [t('articles.create.steps.keywords.name'), t('articles.create.steps.keywords.description')],
  5: (t) => [t('articles.create.steps.review.name'), t('articles.create.steps.review.description')],
};

/**
 * Stepper entries with complete/current/upcoming status. `step` is the wizard
 * step index, which differs from the list position when steps are skipped.
 */
export const buildWizardSteps = (t, currentStep, contentType) => getStepSequence(contentType).map((step) => {
  const [name, description] = STEP_LABELS[step](t);
  return {
    step,
    name,
    description,
    status: currentStep === step ? 'current' : currentStep > step ? 'complete' : 'upcoming'
  };
});

/** Builds the wizard url for a step, keeping article id and content type. */
export const buildStepUrl = (step, { articleId, contentType }) => {
  const stepName = STEP_INDEX_TO_NAME[step];
  const params = new URLSearchParams();
  if (articleId) params.set('id', articleId);
  if (contentType) params.set('type', contentType);
  return `/writer/articles/create-article/step/${stepName}${params.toString() ? '?' + params.toString() : ''}`;
};
